import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save, Send } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Input, TextArea, Select,
    DatePicker, toast
} from '../../components/common';
import { rfqApi, purchaseRequestApi, departmentApi } from '../../services/api';
import './RFQForm.css';

export default function RFQForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const prId = searchParams.get('prId');
    const queryClient = useQueryClient();
    const isEditing = !!id;

    const [form, setForm] = useState({
        title: '',
        description: '',
        departmentId: '',
        purchaseRequestId: prId || '',
        closingDate: '',
        evaluationCriteria: '',
        termsAndConditions: '',
        lines: []
    });
    const [errors, setErrors] = useState({});

    // Fetch existing RFQ if editing
    const { data: rfqData } = useQuery({
        queryKey: ['rfq', id],
        queryFn: () => rfqApi.get(id),
        enabled: isEditing,
        select: (res) => res.data?.data
    });

    // Fetch approved PRs for dropdown
    const { data: approvedPRs = [] } = useQuery({
        queryKey: ['purchaseRequests', 'approved'],
        queryFn: () => purchaseRequestApi.list({ status: 'approved' }),
        select: (res) => res.data?.data || []
    });

    // Fetch PR if creating from PR
    const { data: prData } = useQuery({
        queryKey: ['purchaseRequest', prId],
        queryFn: () => purchaseRequestApi.get(prId),
        enabled: !!prId && !isEditing,
        select: (res) => res.data?.data
    });

    // Fetch departments for dropdown
    const { data: departments = [] } = useQuery({
        queryKey: ['departments'],
        queryFn: () => departmentApi.list(),
        select: (res) => res.data?.data || res.data || []
    });

    useEffect(() => {
        if (rfqData) {
            setForm({
                title: rfqData.title || '',
                description: rfqData.description || '',
                departmentId: rfqData.departmentId || '',
                purchaseRequestId: rfqData.purchaseRequestId?.toString() || '',
                closingDate: rfqData.closingDate?.split('T')[0] || '',
                evaluationCriteria: rfqData.evaluationCriteria || '',
                termsAndConditions: rfqData.termsAndConditions || '',
                lines: rfqData.lines || []
            });
        } else if (prData) {
            // Pre-fill from PR
            setForm(prev => ({
                ...prev,
                title: `RFQ for ${prData.title}`,
                description: prData.description || '',
                departmentId: prData.departmentId || '',
                purchaseRequestId: prId,
                lines: prData.lines?.map(item => ({
                    catalogItemId: item.catalogItemId || item.itemId,
                    description: item.specifications || item.description || '',
                    quantity: item.quantity,
                    itemName: item.itemName || item.catalogItem?.itemName,
                    itemCode: item.itemCode || item.catalogItem?.itemCode,
                    uom: item.uomCode || item.catalogItem?.uom?.code || 'PCS'
                })) || []
            }));
        }
    }, [rfqData, prData, prId]);

    const saveMutation = useMutation({
        mutationFn: (data) => isEditing
            ? rfqApi.update(id, data)
            : rfqApi.create(data),
        onSuccess: (res) => {
            toast.success(`RFQ ${isEditing ? 'updated' : 'created'} successfully`);
            queryClient.invalidateQueries(['rfqs']);
            navigate(`/rfq/${res.data?.data?.id || id}`);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save');
        }
    });

    const handleChange = async (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));

        // Load PR details when PR is selected
        if (name === 'purchaseRequestId' && value) {
            try {
                const res = await purchaseRequestApi.get(value);
                const selectedPR = res.data?.data;
                console.log('Selected PR:', selectedPR);
                console.log('PR Lines:', selectedPR?.lines);
                if (selectedPR) {
                    const mappedLines = selectedPR.lines?.map(item => ({
                        catalogItemId: item.catalogItemId || item.itemId,
                        description: item.specifications || item.description || '',
                        quantity: parseFloat(item.quantity) || 1,
                        itemName: item.itemName || item.catalogItem?.itemName || 'Item',
                        itemCode: item.itemCode || item.catalogItem?.itemCode || '',
                        uom: item.uomCode || item.catalogItem?.uom?.code || 'PCS'
                    })) || [];
                    console.log('Mapped Lines:', mappedLines);
                    setForm(prev => ({
                        ...prev,
                        title: prev.title || `RFQ for ${selectedPR.title}`,
                        description: prev.description || selectedPR.description || '',
                        departmentId: prev.departmentId || selectedPR.departmentId || '',
                        lines: mappedLines
                    }));
                }
            } catch (err) {
                console.error('Failed to load PR details:', err);
            }
        }
    };

    const handleUpdateItem = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            lines: prev.lines.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        }));
    };

    const handleRemoveItem = (index) => {
        setForm(prev => ({
            ...prev,
            lines: prev.lines.filter((_, i) => i !== index)
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.title || form.title.length < 5) newErrors.title = 'Title is required (min 5 characters)';
        if (!form.departmentId) newErrors.departmentId = 'Department is required';
        if (!form.closingDate) newErrors.closingDate = 'Closing date is required';
        if (form.lines.length === 0) newErrors.lines = 'At least one item is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;
        saveMutation.mutate({
            title: form.title,
            description: form.description,
            departmentId: form.departmentId,
            purchaseRequestId: form.purchaseRequestId || null,
            closingDate: new Date(form.closingDate).toISOString(),
            evaluationCriteria: form.evaluationCriteria,
            termsAndConditions: form.termsAndConditions,
            lines: form.lines.map(item => ({
                itemName: item.itemName || item.itemCode || 'Item',
                description: item.description || '',
                quantity: parseFloat(item.quantity) || 1,
                uom: item.uom || 'PCS'
            }))
        });
    };

    return (
        <div className="rfq-form-page">
            <PageHeader
                title={isEditing ? 'Edit RFQ' : 'Create RFQ'}
                breadcrumbs={[
                    { label: 'RFQ', path: '/rfq' },
                    { label: isEditing ? 'Edit' : 'Create' }
                ]}
            />

            <div className="rfq-form-content">
                <Card>
                    <CardBody>
                        <h3>RFQ Details</h3>
                        <div className="form-grid">
                            <div className="form-full">
                                <Input
                                    name="title"
                                    label="RFQ Title"
                                    placeholder="Enter RFQ title"
                                    value={form.title}
                                    onChange={handleChange}
                                    error={errors.title}
                                    required
                                />
                            </div>
                            <Select
                                name="departmentId"
                                label="Department"
                                value={form.departmentId}
                                onChange={handleChange}
                                options={[
                                    { value: '', label: 'Select Department' },
                                    ...departments.map(d => ({ value: d.id, label: d.name }))
                                ]}
                                error={errors.departmentId}
                                required
                            />
                            <DatePicker
                                label="Closing Date"
                                value={form.closingDate}
                                onChange={(date) => setForm(prev => ({ ...prev, closingDate: date }))}
                                minDate={new Date().toISOString().split('T')[0]}
                                error={errors.closingDate}
                                required
                            />
                            <Select
                                name="purchaseRequestId"
                                label="Related PR (Optional)"
                                value={form.purchaseRequestId}
                                onChange={handleChange}
                                options={[
                                    { value: '', label: 'None' },
                                    ...approvedPRs.map(pr => ({
                                        value: pr.id,
                                        label: `${pr.prNumber} - ${pr.title}`
                                    }))
                                ]}
                            />
                            <div className="form-full">
                                <TextArea
                                    name="description"
                                    label="Description"
                                    placeholder="RFQ description and requirements"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                            <div className="form-full">
                                <TextArea
                                    name="evaluationCriteria"
                                    label="Evaluation Criteria"
                                    placeholder="How quotations will be evaluated..."
                                    value={form.evaluationCriteria}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                            <div className="form-full">
                                <TextArea
                                    name="termsAndConditions"
                                    label="Terms & Conditions"
                                    placeholder="Terms and conditions for vendors..."
                                    value={form.termsAndConditions}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <div className="items-header">
                            <h3>RFQ Items</h3>
                        </div>

                        {errors.lines && <p className="items-error">{errors.lines}</p>}

                        {form.lines.length === 0 ? (
                            <div className="items-empty">
                                <p>No items. Select a Related PR to import items automatically.</p>
                            </div>
                        ) : (
                            <div className="items-table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Item</th>
                                            <th>Description</th>
                                            <th>Qty</th>
                                            <th>UOM</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.lines.map((item, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="item-info">
                                                        <span className="item-code">{item.itemCode}</span>
                                                        <span className="item-name">{item.itemName}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={item.description || ''}
                                                        onChange={(e) => handleUpdateItem(index, 'description', e.target.value)}
                                                        placeholder="Specifications..."
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        onChange={(e) => handleUpdateItem(index, 'quantity', e.target.value)}
                                                        className="qty-input"
                                                    />
                                                </td>
                                                <td>{item.uom}</td>
                                                <td>
                                                    <button className="remove-btn" onClick={() => handleRemoveItem(index)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>

                <div className="rfq-form-actions">
                    <Button variant="secondary" onClick={() => navigate('/rfq')}>
                        Cancel
                    </Button>
                    <Button icon={Save} onClick={handleSave} loading={saveMutation.isPending}>
                        {isEditing ? 'Update RFQ' : 'Save RFQ'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
